import React from "react";
import { Outlet, useLocation, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import SidebarNav from "@/components/SidebarNav";
import AdminNav from "@/components/AdminNav";
import AccountSettingsModal from "@/components/AccountSettingsModal";
import { useAccountSettingsModal } from "@/contexts/AccountSettingsModalContext";

const Layout: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, user } = useSelector(
    (state: RootState) => state.auth,
  );
  const { isOpen, closeModal } = useAccountSettingsModal();
  const isAdmin = user?.is_admin === true;
  const isOnAdminRoute = location.pathname.startsWith("/admin");
  const showAdminNav = isAdmin && isOnAdminRoute;
  const disablePageTransition = React.useMemo(() => {
    if (!location.pathname.startsWith("/my-campaigns")) return false;
    if (typeof window === "undefined") return false;

    return window.sessionStorage.getItem("openCreateCampaignModal") === "true";
  }, [location.pathname]);

  if (isAuthenticated) {
    return (
      <div className="relative min-h-screen flex flex-row overflow-x-hidden bg-[#f8f8f8]">
        <SidebarNav />
        <main className="w-[calc(100%_-_62px)] ml-[62px] flex-1 min-h-screen flex flex-col">
          {disablePageTransition ? (
            <div className="flex-1 p-[10px_10px_10px_0px]">
              <div className="relative main-layout main-container border border-[#E3E2E7] bg-white rounded-[15px] backdrop-blur-[10px] h-full">
                {showAdminNav && <AdminNav />}
                <Outlet />
              </div>
            </div>
          ) : (
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.2 }}
                className="flex-1 p-[10px_10px_10px_0px]"
              >
                <div className="relative main-layout main-container border border-[#E3E2E7] bg-white rounded-[15px] backdrop-blur-[10px] h-full">
                  {showAdminNav && <AdminNav />}
                  <Outlet />
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </main>
        <AccountSettingsModal isOpen={isOpen} onClose={closeModal} />
      </div>
    );
  }

  return <Navigate to="/login" replace />;
};

export default Layout;
